import { Spinner } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useMe } from "@/hooks/api";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

export function RouteSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background-normal">
      <Spinner size={32} />
    </div>
  );
}

/**
 * 세 가지 인증 상태, 세 가지 결과:
 *   확인 중  → 스피너 (로그인 화면이 깜빡이지 않도록)
 *   비로그인 → /login, 가려던 곳을 기억해서
 *   로그인   → 요청한 화면
 *
 * **온보딩을 이유로 막지 않아요.** 예전에는 `onboarding` 네 플래그가 모두 true 가 아니면
 * `/onboarding` 으로 돌려보냈는데, 그 중 `first_strategy_created` 는 규칙을 하나 만들어야
 * 서야 true 가 돼요. 그래서 키를 등록하고 IP까지 확인한 뒤에도 대시보드에 못 들어가고,
 * 로그인이 유지되지 않는 것처럼 보였어요. 키 등록은 이제 대시보드에서 안내하는
 * **별도 절차**이고, 라우터는 인증만 봅니다.
 */
export function PrivateRoute({
  children,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const me = useMe();

  if (isLoading) return <RouteSpinner />;
  if (!isAuthenticated) {
    // 쿼리까지 기억해요 — `/connect?request=…` 는 쿼리가 곧 요청이에요(`lib/return-to.ts`).
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  // 역할 판정만 `me` 를 기다려요.
  if (requireAdmin) {
    if (me.isLoading) return <RouteSpinner />;
    if (me.data?.role !== "admin") return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
